import { APP_INITIALIZER, ApplicationConfig, ErrorHandler, importProvidersFrom, LOCALE_ID } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_SELECT_CONFIG, MatSelectConfig } from '@angular/material/select';
import { MAT_AUTOCOMPLETE_DEFAULT_OPTIONS, MatAutocompleteDefaultOptions } from '@angular/material/autocomplete';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { OWL_DATE_TIME_FORMATS, OWL_DATE_TIME_LOCALE, OwlDateTimeIntl } from '@danielmoncada/angular-datetime-picker';
import { OwlMomentDateTimeModule } from '@danielmoncada/angular-datetime-picker-moment-adapter';
import { CzechOwlDateTimeIntl } from './utils/date-time/czech-owl-date-time-intl';
import { provideRouter, withPreloading, NoPreloading, withInMemoryScrolling, Router } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldDefaultOptions } from '@angular/material/form-field';
import { HttpClient } from '@angular/common/http';
import { registerLocaleData } from '@angular/common';
import localeCs from '@angular/common/locales/cs';
import * as Sentry from '@sentry/angular';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { ToastrModule, ToastrService } from 'ngx-toastr';
import { AngularSvgIconModule } from 'angular-svg-icon';

import { appRoutes } from './app.routing';
import { IconsModule } from './core/icons/icons.module';
import { TranslocoCoreModule } from './core/transloco/transloco.module';
import { AppPlatformModule } from './utils/app-platform/app-platform.module';
import { AuthModule } from './auth/auth.module';
import { BASE_PATH } from './api/variables';
import { ErrorModule } from './error/error.module';
import { LanguageModule } from './utils/language/language.module';
import { ResponseHeadersModule } from './utils/response-headers-interceptor/response-headers.module';
import { NgxTranslateCacheBusterLoader } from './core/transloco/ngx-translate-cache-buster.loader';
import { ChunkErrorReloadService } from './core/chunk-error/chunk-error-reload.service';
import { LanguageService } from './utils/language/language.service';
import { VipToastrService } from './core/toast/vip-toastr.service';
import { SplashScreenService } from './core/splash-screen/splash-screen.service';
import { loadingInterceptor } from './core/loading/loading.service';
import { environment } from '../environments/environment';

registerLocaleData(localeCs);

export const applicationConfig: ApplicationConfig = {
    providers: [
        provideRouter(
            appRoutes,
            withPreloading(NoPreloading),
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
        ),
        provideAnimations(),
        // loadingInterceptor = horní loading bar (`app-loading-bar`)
        provideHttpClient(withInterceptorsFromDi(), withInterceptors([loadingInterceptor])),

        importProvidersFrom(
            // Auth, icons, transloco, error handling, language, response-headers interceptors
            AuthModule,
            IconsModule,
            TranslocoCoreModule,
            ErrorModule,
            LanguageModule,
            ResponseHeadersModule,
            AppPlatformModule,
            // Moment date adapter (provides DateAdapter globally for MatDatepicker)
            MatMomentDateModule,
            // Owl DateTime Picker moment adapter (provides DateTimePicker service globally)
            OwlMomentDateTimeModule,
            // Toastr notifications – vzhled v src/styles/_vip-toast.scss
            ToastrModule.forRoot({
                closeButton: true,
                timeOut: 5000,
                extendedTimeOut: 2000,
                // Stejná hláška (např. interceptor + komponenta) se nezdvojí, jen prodlouží
                preventDuplicates: true,
                resetTimeoutOnDuplicate: true,
                includeTitleDuplicates: true,
                maxOpened: 4,
                autoDismiss: true,
                newestOnTop: true
            }),
            // SVG icons
            AngularSvgIconModule.forRoot(),
            // NGX Translate
            TranslateModule.forRoot({
                loader: {
                    provide: TranslateLoader,
                    useFactory: (http: HttpClient) => new NgxTranslateCacheBusterLoader(http),
                    deps: [HttpClient],
                },
            }),
        ),

        // Výchozí vzhled polí `fill` (dřív nastavoval FuseModule)
        { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'fill' } satisfies MatFormFieldDefaultOptions },
        // Slučování dvojitých chybových toastů (interceptor + komponenta) – viz VipToastrService
        { provide: ToastrService, useExisting: VipToastrService },

        { provide: BASE_PATH, useValue: environment.apiBasePath },
        { provide: LOCALE_ID, useValue: 'cs' },
        { provide: MAT_DATE_LOCALE, useValue: 'cs-CZ' },
        // Jednotný vzhled rozbalovacích seznamů (styl .vip-select-overlay v _vip-form.scss);
        // u jednoduchého výběru bez fajfky – vybraná položka je zvýrazněná zlatě
        {
            provide: MAT_SELECT_CONFIG,
            useValue: { overlayPanelClass: 'vip-select-overlay', hideSingleSelectionIndicator: true } satisfies MatSelectConfig,
        },
        {
            provide: MAT_AUTOCOMPLETE_DEFAULT_OPTIONS,
            useValue: { overlayPanelClass: 'vip-select-overlay', hideSingleSelectionIndicator: true } satisfies MatAutocompleteDefaultOptions,
        },
        { provide: OWL_DATE_TIME_LOCALE, useValue: 'cs-CZ' },
        { provide: OwlDateTimeIntl, useClass: CzechOwlDateTimeIntl },
        {
            provide: OWL_DATE_TIME_FORMATS,
            useValue: {
                parseInput: 'DD. M. YYYY HH:mm',
                fullPickerInput: 'DD. M. YYYY HH:mm',
                datePickerInput: 'DD. M. YYYY',
                timePickerInput: 'HH:mm',
                monthYearLabel: 'MMM YYYY',
                dateA11yLabel: 'D. M. YYYY',
                monthYearA11yLabel: 'MMMM YYYY',
            },
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: { dateInput: 'DD. M. YYYY HH:mm' },
                display: {
                    dateInput: 'D. M. YYYY',
                    monthYearLabel: 'MMM YYYY',
                    dateA11yLabel: 'D. M. YYYY',
                    monthYearA11yLabel: 'MMMM YYYY',
                },
            },
        },
        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler(),
        },
        {
            provide: Sentry.TraceService,
            deps: [Router],
        },
        {
            provide: APP_INITIALIZER,
            useFactory: () => () => {},
            deps: [Sentry.TraceService],
            multi: true,
        },
        {
            provide: APP_INITIALIZER,
            useFactory: (languageService: LanguageService) =>
                () => firstValueFrom(languageService.initAppLanguage()),
            deps: [LanguageService],
            multi: true,
        },
        {
            provide: APP_INITIALIZER,
            useFactory: (chunkErrorReloadService: ChunkErrorReloadService) =>
                () => chunkErrorReloadService.init(),
            deps: [ChunkErrorReloadService],
            multi: true,
        },
        {
            // Úvodní obrazovka se schová po první navigaci – service musí vzniknout hned při startu
            provide: APP_INITIALIZER,
            useFactory: () => () => {},
            deps: [SplashScreenService],
            multi: true,
        },
    ],
};
